// "No account" mode: the data of someone using Wallet without an account lives in this browser (localStorage).
// There can be several local profiles (personal / business), each with its own data. Per API branch the list is kept
// exactly as the server would return it; items created here have a negative id.

export const BRANCHES = ["accounts", "categories", "cards/templates", "cards", "transactions", "transfers", "states"] as const;

/** A profile as the API returns it (`/api/profiles/`); the local ones have the same shape. */
export interface Profile {
    id: number;
    name: string;
    is_business: boolean;
    order: number;
}

const PREFIX = "wallet.";
const GUEST_KEY = PREFIX + "guest";
const PROFILES_KEY = PREFIX + "guest.profiles";
const CURRENT_PROFILE_KEY = PREFIX + "guest.profile";

function read(key: string): string | null {
    try { return localStorage.getItem(key); } catch { return null; }
}
function write(key: string, value: string) {
    try { localStorage.setItem(key, value); } catch { /* storage unavailable or full */ }
}

/** The first local profile keeps the name "guest" so data saved before profiles existed is still its data. */
export function guestNamespace(profileId: number): string {
    return profileId === 1 ? "guest" : `guest-${profileId}`;
}

let namespace = guestNamespace(1);

export const LocalStore = {
    // MARK: Session

    /** The browser remembers that this person chose "Continua senza account". */
    isGuestSession(): boolean {
        return read(GUEST_KEY) === "1";
    },

    setGuestSession(value: boolean) {
        try {
            if (value) localStorage.setItem(GUEST_KEY, "1");
            else localStorage.removeItem(GUEST_KEY);
        } catch { /* storage unavailable */ }
    },

    // MARK: Local profiles

    guestProfiles(): Profile[] {
        const raw = read(PROFILES_KEY);
        if (raw) {
            try {
                const list = JSON.parse(raw) as Profile[];
                if (Array.isArray(list) && list.length > 0) return list.sort((a, b) => a.order - b.order || a.id - b.id);
            } catch { /* corrupted: start again */ }
        }
        return [{ id: 1, name: "Personale", is_business: false, order: 0 }];
    },

    saveGuestProfiles(list: Profile[]) {
        write(PROFILES_KEY, JSON.stringify(list));
    },

    currentGuestProfileId(): number {
        const id = Number(read(CURRENT_PROFILE_KEY));
        const list = LocalStore.guestProfiles();
        return list.some((p) => p.id === id) ? id : list[0]!.id;
    },

    setCurrentGuestProfile(id: number) {
        write(CURRENT_PROFILE_KEY, String(id));
    },

    /** Chooses which local profile the collections below belong to. */
    useGuestProfile(id: number) {
        namespace = guestNamespace(id);
    },

    // MARK: Data of the current local profile

    loadCollection<T = any>(branch: string): T[] | null {
        const raw = read(`${PREFIX}data.${namespace}.${branch}`);
        if (raw === null) return null;
        try {
            const list = JSON.parse(raw);
            return Array.isArray(list) ? list : null;
        } catch {
            return null;
        }
    },

    saveCollection(branch: string, items: unknown[]) {
        write(`${PREFIX}data.${namespace}.${branch}`, JSON.stringify(items));
    },

    /** Deletes everything saved without an account: every local profile and its data. */
    clearGuestData() {
        try {
            const keys: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (key.startsWith(`${PREFIX}data.guest`) || key === PROFILES_KEY || key === CURRENT_PROFILE_KEY)) keys.push(key);
            }
            keys.forEach((key) => localStorage.removeItem(key));
        } catch { /* storage unavailable */ }
    },
};

/** "YYYY-MM-DD HH:MM:SS" (what the API accepts) -> the epoch of that text read as UTC (what the API returns). */
export function sqlToEpoch(value: string): number {
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(value);
    if (!m) return Math.floor(Date.now() / 1000);
    return Math.floor(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!, +(m[6] ?? 0)) / 1000);
}
